import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Button, Modal, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { orderAPI } from '../utils/api';
import './MyOrders.css';

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [detailModal, setDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    loadOrders();
  }, [pagination.current]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const allOrders = await orderAPI.getMyOrders();
      
      // 前端分页
      const start = (pagination.current - 1) * pagination.pageSize;
      const end = start + pagination.pageSize;
      setOrders(allOrders.slice(start, end));
      setPagination({
        ...pagination,
        total: allOrders.length
      });
    } catch (error) {
      message.error('加载订单失败');
    } finally {
      setLoading(false);
    }
  };

  const showDetail = (order) => {
    setSelectedOrder(order);
    setDetailModal(true);
  };

  const getStatusTag = (status) => {
    const statusMap = {
      pending: { text: '待支付', color: 'warning' },
      paid: { text: '已支付', color: 'processing' },
      completed: { text: '已完成', color: 'success' },
      cancelled: { text: '已取消', color: 'default' },
      refunded: { text: '已退款', color: 'error' }
    };
    const config = statusMap[status] || { text: status, color: 'default' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: '订单号',
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 200
    },
    {
      title: '商品名称',
      dataIndex: 'productName',
      key: 'productName'
    },
    {
      title: '数量',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center'
    },
    {
      title: '金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 100,
      render: (price) => <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>¥{price?.toFixed(2)}</span>
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => getStatusTag(status)
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (time) => new Date(time).toLocaleString('zh-CN')
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => showDetail(record)}
        >
          查看
        </Button>
      )
    }
  ];

  return (
    <div className="my-orders-page">
      <Card title="我的订单">
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            onChange: (page) => setPagination({ ...pagination, current: page })
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title="订单详情"
        open={detailModal}
        onCancel={() => setDetailModal(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModal(false)}>
            关闭
          </Button>
        ]}
        width={600}
      >
        {selectedOrder && (
          <div className="order-detail">
            <div className="detail-item">
              <span className="label">订单号:</span>
              <span className="value">{selectedOrder.orderNo}</span>
            </div>
            <div className="detail-item">
              <span className="label">商品名称:</span>
              <span className="value">{selectedOrder.productName}</span>
            </div>
            <div className="detail-item">
              <span className="label">购买数量:</span>
              <span className="value">{selectedOrder.quantity}</span>
            </div>
            <div className="detail-item">
              <span className="label">订单金额:</span>
              <span className="value price">¥{selectedOrder.totalAmount?.toFixed(2)}</span>
            </div>
            <div className="detail-item">
              <span className="label">订单状态:</span>
              <span className="value">{getStatusTag(selectedOrder.status)}</span>
            </div>
            <div className="detail-item">
              <span className="label">创建时间:</span>
              <span className="value">
                {new Date(selectedOrder.createdAt).toLocaleString('zh-CN')}
              </span>
            </div>
            {selectedOrder.paidAt && (
              <div className="detail-item">
                <span className="label">支付时间:</span>
                <span className="value">
                  {new Date(selectedOrder.paidAt).toLocaleString('zh-CN')}
                </span>
              </div>
            )}
            {selectedOrder.contactEmail && (
              <div className="detail-item">
                <span className="label">联系邮箱:</span>
                <span className="value">{selectedOrder.contactEmail}</span>
              </div>
            )}
            
            {selectedOrder.deliveryInfo && selectedOrder.status === 'paid' && (
              <div className="delivery-section" style={{
                marginTop: '20px',
                padding: '16px',
                background: '#f6ffed',
                border: '2px solid #b7eb8f',
                borderRadius: '8px'
              }}>
                <h4 style={{ color: '#52c41a', marginBottom: '12px' }}>🎁 卡密信息</h4>
                <pre className="delivery-content" style={{
                  background: '#fff',
                  padding: '12px',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  userSelect: 'all'
                }}>
                  {selectedOrder.deliveryInfo}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MyOrders;
